import { useCallback, useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { authAtom } from "../../../lib/auth-store.js";
import { apiCall } from "../../../lib/api-client.js";
import { useApiQuery } from "../../../lib/useApiQuery.js";
import {
  rolesPath,
  roleByIdPath,
  leadershipPath,
  leadershipByRolePath,
  usersPath,
  feishuDepartmentsPath,
} from "../../../lib/api-paths.js";
import {
  ApiError,
  type FeishuDepartmentsResponse,
  type FeishuDeptNode,
  type LeadershipRow,
  type PublicRole,
  type UsersListResponse,
} from "../../../lib/api-types.js";
import { ErrorBanner } from "../../../components/ErrorBanner.js";
import { InlineLoading } from "../../../components/Spinner.js";
import { Empty } from "../../../components/Empty.js";
import { Modal } from "../../../components/Modal.js";
import { PrimaryButton, SecondaryButton } from "../../../components/FormField.js";
import { FeishuDeptTree } from "../../OrganizationDetail/FeishuDeptTree.js";
import { cn } from "../../../lib/cn.js";
import type { OnboardingStepProps } from "../types.js";
import { isAdminRole } from "../shared.js";
import { UserPickerModal } from "../UserPickerModal.js";

interface RoleMappingState {
  deptIds: string[];
  leaderIds: string[];
}

/** 把部门树拍平成 id→name 与 id→后代 ids（叶子继承用）。 */
function indexTree(tree: FeishuDeptNode[]): {
  nameById: Map<string, string>;
  descendantsById: Map<string, string[]>;
} {
  const nameById = new Map<string, string>();
  const descendantsById = new Map<string, string[]>();
  const walk = (node: FeishuDeptNode): string[] => {
    nameById.set(node.openDepartmentId, node.name);
    const desc: string[] = [];
    for (const child of node.children) {
      desc.push(child.openDepartmentId, ...walk(child));
    }
    descendantsById.set(node.openDepartmentId, desc);
    return desc;
  };
  tree.forEach(walk);
  return { nameById, descendantsById };
}

/**
 * Step 4 — 角色 ↔ 飞书部门 ↔ 负责人 原子关联。
 * 部门写 role.suggestedFeishuDeptIds；负责人写 role_leaderships（= 审批人，复用一份）。
 * 就绪判定：每个非管理员角色至少 1 部门 + 1 负责人。
 */
export function Step4Mapping({
  org,
  onReadyChange,
}: OnboardingStepProps): JSX.Element {
  const auth = useAtomValue(authAtom);
  const { data: roles, loading: rolesLoading } = useApiQuery<PublicRole[]>(
    rolesPath(org.code),
    [org.code],
  );
  const { data: leadership } = useApiQuery<LeadershipRow[]>(
    leadershipPath(org.code),
    [org.code],
  );
  const { data: usersResp } = useApiQuery<UsersListResponse>(
    `${usersPath(org.code)}?pageSize=200&status=active`,
    [org.code],
  );
  const { data: deptResp } = useApiQuery<FeishuDepartmentsResponse>(
    feishuDepartmentsPath(org.code),
    [org.code],
  );

  const [stateByRole, setStateByRole] = useState<Record<string, RoleMappingState>>(
    {},
  );
  const [seeded, setSeeded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deptPickerRole, setDeptPickerRole] = useState<PublicRole | null>(null);
  const [leaderPickerRole, setLeaderPickerRole] = useState<PublicRole | null>(
    null,
  );

  const treeIndex = useMemo(
    () => indexTree(deptResp?.tree ?? []),
    [deptResp?.tree],
  );
  const userName = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of usersResp?.rows ?? []) {
      m.set(u.id, u.feishu?.name ? `${u.feishu.name}` : u.user);
    }
    return m;
  }, [usersResp?.rows]);

  // 一次性 seed：roles + leadership 都到位后填充本地编辑态
  useEffect(() => {
    if (seeded || !roles || !leadership) return;
    const leaderByCode = new Map(
      leadership.map((l) => [l.roleCode, l.leaderUserIds]),
    );
    const next: Record<string, RoleMappingState> = {};
    for (const r of roles) {
      next[r.id] = {
        deptIds: [...r.suggestedFeishuDeptIds],
        leaderIds: [...(leaderByCode.get(r.code) ?? [])],
      };
    }
    setStateByRole(next);
    setSeeded(true);
  }, [seeded, roles, leadership]);

  const nonAdminRoles = useMemo(
    () => (roles ?? []).filter((r) => !isAdminRole(r)),
    [roles],
  );

  // 就绪：每个非管理员角色至少 1 部门 + 1 负责人
  useEffect(() => {
    if (!seeded || nonAdminRoles.length === 0) {
      onReadyChange(false);
      return;
    }
    const ok = nonAdminRoles.every((r) => {
      const s = stateByRole[r.id];
      return s && s.deptIds.length > 0 && s.leaderIds.length > 0;
    });
    onReadyChange(ok);
  }, [seeded, nonAdminRoles, stateByRole, onReadyChange]);

  const saveDepts = useCallback(
    async (role: PublicRole, deptIds: string[]) => {
      if (!auth) return;
      setStateByRole((prev) => ({
        ...prev,
        [role.id]: { ...prev[role.id], deptIds },
      }));
      try {
        await apiCall("PATCH", roleByIdPath(org.code, role.id), {
          token: auth.token,
          body: { suggestedFeishuDeptIds: deptIds },
        });
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "保存部门失败");
      }
    },
    [auth, org.code],
  );

  const saveLeaders = useCallback(
    async (role: PublicRole, leaderIds: string[]) => {
      if (!auth) return;
      setStateByRole((prev) => ({
        ...prev,
        [role.id]: { ...prev[role.id], leaderIds },
      }));
      try {
        await apiCall("PUT", leadershipByRolePath(org.code, role.code), {
          token: auth.token,
          body: { leaderUserIds: leaderIds },
        });
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "保存负责人失败");
      }
    },
    [auth, org.code],
  );

  if (rolesLoading || !seeded) {
    return <InlineLoading label="加载角色映射…" />;
  }
  if ((roles?.length ?? 0) === 0) {
    return (
      <Empty
        title="还没有角色"
        description="请先回到上一步「生成角色」，再来关联部门与负责人。"
      />
    );
  }

  return (
    <div className="space-y-3">
      {error ? (
        <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner>
      ) : null}
      <ul className="space-y-3">
        {(roles ?? []).map((role) => {
          const s = stateByRole[role.id] ?? { deptIds: [], leaderIds: [] };
          const admin = isAdminRole(role);
          return (
            <li
              key={role.id}
              className="rounded-lg border border-dosia-border bg-dosia-bg-light p-4"
            >
              <div className="mb-3 flex items-center gap-2">
                <span className="text-sm font-semibold text-dosia-text">
                  {role.name}
                </span>
                <span className="font-mono text-xs text-dosia-text-dim">
                  {role.code}
                </span>
                {admin ? (
                  <span className="rounded-full bg-dosia-bg-lighter px-2 py-0.5 text-[10px] text-dosia-text-secondary">
                    管理员角色 · 下一步设置
                  </span>
                ) : null}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ChipField
                  label="关联部门"
                  emptyHint={admin ? "管理员角色无需关联部门" : "未关联部门"}
                  chips={s.deptIds.map(
                    (id) => treeIndex.nameById.get(id) ?? id,
                  )}
                  onEdit={admin ? undefined : () => setDeptPickerRole(role)}
                  needAttention={!admin && s.deptIds.length === 0}
                />
                <ChipField
                  label="负责人"
                  emptyHint={admin ? "管理员在下一步指定" : "未指定负责人"}
                  chips={s.leaderIds.map(
                    (id) => userName.get(id) ?? id.slice(-6),
                  )}
                  onEdit={admin ? undefined : () => setLeaderPickerRole(role)}
                  needAttention={!admin && s.leaderIds.length === 0}
                />
              </div>
            </li>
          );
        })}
      </ul>

      {deptPickerRole ? (
        <DeptPickerModal
          role={deptPickerRole}
          initial={stateByRole[deptPickerRole.id]?.deptIds ?? []}
          descendantsById={treeIndex.descendantsById}
          onCancel={() => setDeptPickerRole(null)}
          onSave={async (deptIds) => {
            await saveDepts(deptPickerRole, deptIds);
            setDeptPickerRole(null);
          }}
        />
      ) : null}

      {leaderPickerRole ? (
        <UserPickerModal
          title={`指定负责人 — ${leaderPickerRole.name}`}
          orgCode={org.code}
          initial={stateByRole[leaderPickerRole.id]?.leaderIds ?? []}
          saving={false}
          onCancel={() => setLeaderPickerRole(null)}
          onSave={async (leaderIds) => {
            await saveLeaders(leaderPickerRole, leaderIds);
            setLeaderPickerRole(null);
          }}
        />
      ) : null}
    </div>
  );
}

function ChipField({
  label,
  chips,
  emptyHint,
  onEdit,
  needAttention,
}: {
  label: string;
  chips: string[];
  emptyHint: string;
  onEdit?: () => void;
  needAttention?: boolean;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-dosia-text-secondary">
          {label}
        </span>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="text-xs text-dosia-accent hover:underline"
          >
            编辑
          </button>
        ) : null}
      </div>
      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {chips.map((c, i) => (
            <span
              key={`${c}-${i}`}
              className="rounded-full bg-dosia-bg-lighter px-2 py-0.5 text-xs text-dosia-text"
            >
              {c}
            </span>
          ))}
        </div>
      ) : (
        <p
          className={cn(
            "text-xs",
            needAttention ? "text-amber-700" : "text-dosia-text-dim",
          )}
        >
          {emptyHint}
        </p>
      )}
    </div>
  );
}

function DeptPickerModal({
  role,
  initial,
  descendantsById,
  onCancel,
  onSave,
}: {
  role: PublicRole;
  initial: string[];
  descendantsById: Map<string, string[]>;
  onCancel: () => void;
  onSave: (deptIds: string[]) => Promise<void>;
}): JSX.Element {
  const [selected, setSelected] = useState<string[]>(initial);
  const [saving, setSaving] = useState(false);

  // 叶子继承：勾选父部门时，其所有子部门一并勾选；取消同理。
  const toggle = useCallback(
    (id: string) => {
      setSelected((curr) => {
        const set = new Set(curr);
        const family = [id, ...(descendantsById.get(id) ?? [])];
        if (set.has(id)) {
          family.forEach((x) => set.delete(x));
        } else {
          family.forEach((x) => set.add(x));
        }
        return Array.from(set);
      });
    },
    [descendantsById],
  );

  return (
    <Modal
      open
      onClose={onCancel}
      title={`关联部门 — ${role.name}`}
      size="lg"
      footer={
        <>
          <SecondaryButton onClick={onCancel}>取消</SecondaryButton>
          <PrimaryButton
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              await onSave(selected);
              setSaving(false);
            }}
          >
            保存（已选 {selected.length}）
          </PrimaryButton>
        </>
      }
    >
      <p className="mb-2 text-xs text-dosia-text-dim">
        勾选一个父部门会自动选上它下面的所有子部门，你也可以单独取消。
      </p>
      <div className="max-h-[360px] overflow-auto">
        <FeishuDeptTree
          mode="multi"
          selected={selected}
          onToggle={toggle}
          hideSyncHeader
        />
      </div>
    </Modal>
  );
}
